import { request } from '../../Common/api/client';

/**
 * GET /api/admin/cp - 가맹점 목록
 * @param {object} params - q(검색어), status, page, limit 등
 */
export async function getAdminCpList(params = {}) {
  return request({ method: 'GET', url: '/api/admin/cp', params });
}

/**
 * POST /api/admin/cp - 가맹점 신규 생성
 * @param {object} body - {
 *   cpId, name, bossName, businessNo, phone, tempPassword, feeRate (required),
 *   address (사업장 주소, optional),
 *   bankName/bank_name, accountNumber/bank_account_number, accountHolder/bank_account_holder (optional)
 * }
 * @returns {Promise<{cp_id, cpId, tempPassword}>}
 */
export async function createAdminCp(body) {
  const {
    bankName, bank_name,
    bankCode, bank_code,
    accountNumber, bankAccountNumber,
    accountHolder, bankAccountHolder,
    ...rest
  } = body;
  const data = {
    ...rest,
    address: body.address ?? rest.address ?? '',
    bankName: bank_name ?? bankName ?? '',
    bankCode: bank_code ?? bankCode ?? '',
    bankAccountNumber: body.bank_account_number ?? bankAccountNumber ?? accountNumber ?? '',
    bankAccountHolder: body.bank_account_holder ?? bankAccountHolder ?? accountHolder ?? '',
  };
  return request({ method: 'POST', url: '/api/admin/cp', data });
}

/**
 * GET /api/admin/cp/detail - 가맹점 상세정보
 * @param {number|string} id - 가맹점 ID (cp.id)
 */
export async function getAdminCpDetail(id) {
  return request({
    method: 'GET',
    url: '/api/admin/cp/detail',
    params: { id },
  });
}

/**
 * POST /api/admin/cp/update - 가맹점 상세정보 수정
 * @param {object} body - 수정할 필드 (id 필수), address, feeRate (수수료율), bankName/bankCode/accountNumber/accountHolder 등
 */
export async function updateAdminCp(body) {
  const {
    bankName, bank_name,
    bankCode, bank_code,
    accountNumber, bankAccountNumber,
    accountHolder, bankAccountHolder,
    feeRate, fee_rate,
    ...rest
  } = body;
  const data = {
    ...rest,
    address: body.address ?? rest.address ?? '',
    feeRate: fee_rate ?? feeRate ?? rest.feeRate ?? '',
    bankName: bank_name ?? bankName ?? '',
    bankCode: bank_code ?? bankCode ?? '',
    bankAccountNumber: body.bank_account_number ?? bankAccountNumber ?? accountNumber ?? '',
    bankAccountHolder: body.bank_account_holder ?? bankAccountHolder ?? accountHolder ?? '',
  };
  return request({
    method: 'POST',
    url: '/api/admin/cp/update',
    data,
  });
}

/**
 * POST /api/admin/cp/reset-password - 가맹점 비밀번호 변경
 * @param {object} body - { cpId, password }
 */
export async function resetAdminCpPassword(body) {
  return request({
    method: 'POST',
    url: '/api/admin/cp/reset-password',
    data: body,
  });
}

/** GET /api/admin/cp/:cpPkId/payment-link-access - CP별 URL 결제 허용 설정 조회 */
export async function getAdminCpPaymentLinkAccess(cpPkId) {
  return request({
    method: 'GET',
    url: `/api/admin/cp/${encodeURIComponent(cpPkId)}/payment-link-access`,
  });
}

/** PATCH /api/admin/cp/:cpPkId/payment-link-access - CP별 URL 결제 허용 설정 변경 */
export async function updateAdminCpPaymentLinkAccess(cpPkId, enabled) {
  return request({
    method: 'PATCH',
    url: `/api/admin/cp/${encodeURIComponent(cpPkId)}/payment-link-access`,
    data: { enabled: enabled === true },
  });
}

function adminCpTerminalPath(cpId, suffix = '') {
  const base = `/api/admin/cp/${encodeURIComponent(cpId)}/terminals`;
  return suffix ? `${base}/${encodeURIComponent(suffix)}` : base;
}

/** GET /api/admin/cp/:cpId/terminals - 특정 CP 소속 영업자 목록 */
export async function getAdminCpTerminals(cpId, params = {}) {
  return request({ method: 'GET', url: adminCpTerminalPath(cpId), params });
}

/** POST /api/admin/cp/:cpId/terminals - 특정 CP에 영업자 등록 */
export async function createAdminCpTerminal(cpId, body) {
  return request({ method: 'POST', url: adminCpTerminalPath(cpId), data: body });
}

/** DELETE /api/admin/cp/:cpId/demo-terminals/:terminalId - 시연용 영업자 및 시연 데이터 삭제 */
export async function deleteAdminCpDemoTerminal(cpId, terminalId) {
  return request({
    method: 'DELETE',
    url: `/api/admin/cp/${encodeURIComponent(cpId)}/demo-terminals/${encodeURIComponent(terminalId)}`,
  });
}


/** PATCH /api/admin/cp/:cpId/terminals/:terminalId - 영업자 정보·상태 수정 */
export async function updateAdminCpTerminal(cpId, terminalId, body) {
  return request({
    method: 'PATCH',
    url: adminCpTerminalPath(cpId, terminalId),
    data: body,
  });
}

/** POST /api/admin/cp/:cpId/terminals/:terminalId/reset-password */
export async function resetAdminCpTerminalPassword(cpId, terminalId, newPassword) {
  return request({
    method: 'POST',
    url: `${adminCpTerminalPath(cpId, terminalId)}/reset-password`,
    data: { newPassword },
  });
}

/** 관리자가 거래처 담당 영업자를 변경한다. */
export async function updateAdminCustomerAssignment(cpId, mappingId, terminalPkId) {
  return request({
    method: 'PATCH',
    url: `/api/admin/cp/${encodeURIComponent(cpId)}/customers/${encodeURIComponent(mappingId)}/assignment`,
    data: { terminalPkId },
  });
}
